import { useState } from 'react';
import { betrag, type Warnung } from '@beholder/shared/module/budget';

/** Selbst gezeichnete Diagramme (keine Bibliothek). Farben nur aus den Variablen von stil.css. */

const WARN_TEXT: Record<Warnung, string> = {
  ok: '',
  knapp: 'fast aufgebraucht',
  angebote: 'mit offenen Angeboten über Budget',
  ueberschritten: 'überschritten',
};

/** Plan-gegen-Ist-Leiste: Ist (gefüllt), offene Angebote (schraffiert), Plan = volle Breite. */
export function PlanLeiste({
  planCent,
  istCent,
  angeboteCent,
  warnung,
  waehrung,
  gross,
}: {
  planCent: number | null;
  istCent: number;
  angeboteCent: number;
  warnung: Warnung;
  waehrung: string;
  gross?: boolean;
}) {
  // Maßstab: das Größere aus Plan und Ist+Angebote, damit Überschreitungen sichtbar bleiben
  const max = Math.max(planCent ?? 0, istCent + angeboteCent, 1);
  const ist = Math.max(0, (istCent / max) * 100);
  const ang = Math.max(0, (angeboteCent / max) * 100);
  const plan = planCent === null ? null : (planCent / max) * 100;
  const titel = [
    `Ausgegeben ${betrag(istCent, waehrung)}`,
    angeboteCent ? `offene Angebote ${betrag(angeboteCent, waehrung)}` : '',
    planCent !== null ? `Budget ${betrag(planCent, waehrung)}` : 'kein Budget festgelegt',
    WARN_TEXT[warnung],
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={`bu-leiste${gross ? ' bu-leiste-gross' : ''} bu-w-${warnung}`} title={titel} role="img" aria-label={titel}>
      <div className="bu-leiste-ist" style={{ width: `${ist}%` }} />
      {ang > 0 && <div className="bu-leiste-angebot" style={{ left: `${ist}%`, width: `${ang}%` }} />}
      {plan !== null && plan < 100 && <div className="bu-leiste-plan" style={{ left: `${plan}%` }} />}
    </div>
  );
}

export function WarnMarke({ warnung, ueberverteilt }: { warnung: Warnung; ueberverteilt?: boolean }) {
  return (
    <>
      {warnung === 'ueberschritten' && <span className="bu-marke bu-marke-gefahr">⚠ überschritten</span>}
      {warnung === 'angebote' && <span className="bu-marke bu-marke-warn">⚠ mit Angeboten über Budget</span>}
      {warnung === 'knapp' && <span className="bu-marke bu-marke-warn">fast aufgebraucht</span>}
      {ueberverteilt && <span className="bu-marke bu-marke-warn" title="Die Unterkategorien haben zusammen mehr Budget als diese Kategorie">Unterkategorien über Budget</span>}
    </>
  );
}

export interface Wertzeile {
  schluessel: string;
  name: string;
  wert: number;
  zusatz?: number;
}

/** Waagerechte Balken, z. B. Ausgaben je Teilnehmer. `zusatz` = offene Angebote (schraffiert). */
export function Balken({ zeilen, waehrung, zusatzName = 'offene Angebote' }: { zeilen: Wertzeile[]; waehrung: string; zusatzName?: string }) {
  const max = Math.max(1, ...zeilen.map((z) => z.wert + (z.zusatz ?? 0)));
  if (!zeilen.length) return <p className="leise">Noch keine Daten.</p>;
  return (
    <ul className="bu-balken">
      {zeilen.map((z) => {
        const t = `${z.name}: ${betrag(z.wert, waehrung)}${z.zusatz ? `, ${zusatzName} ${betrag(z.zusatz, waehrung)}` : ''}`;
        return (
          <li key={z.schluessel} title={t}>
            <span className="bu-balken-name">{z.name}</span>
            <span className="bu-balken-spur" aria-hidden>
              <span className="bu-balken-wert" style={{ width: `${(Math.max(0, z.wert) / max) * 100}%` }} />
              {!!z.zusatz && <span className="bu-balken-zusatz" style={{ width: `${(z.zusatz / max) * 100}%` }} />}
            </span>
            <span className="bu-balken-zahl">
              {betrag(z.wert, waehrung)}
              {!!z.zusatz && <span className="leise klein"> + {betrag(z.zusatz, waehrung)}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const KREIS_FARBEN = [
  'var(--akzent)',
  'color-mix(in srgb, var(--akzent) 62%, var(--flaeche))',
  'color-mix(in srgb, var(--akzent) 34%, var(--flaeche))',
  'var(--text-leise)',
  'color-mix(in srgb, var(--text-leise) 60%, var(--flaeche))',
  'color-mix(in srgb, var(--text-leise) 32%, var(--flaeche))',
];

/** Ring (Kreisdiagramm) mit Legende; mehr als sechs Teile werden zu „Sonstige“ zusammengefasst. */
export function Kreis({ teile, waehrung, leer = 'Noch keine Ausgaben.', name = 'Anteile der Ausgaben' }: { teile: Wertzeile[]; waehrung: string; leer?: string; name?: string }) {
  const [aktiv, setAktiv] = useState<string | null>(null);
  const positiv = teile.filter((t) => t.wert > 0).sort((a, b) => b.wert - a.wert);
  let liste = positiv;
  if (positiv.length > KREIS_FARBEN.length) {
    const rest = positiv.slice(KREIS_FARBEN.length - 1);
    liste = [...positiv.slice(0, KREIS_FARBEN.length - 1), { schluessel: '__rest', name: `Sonstige (${rest.length})`, wert: rest.reduce((s, r) => s + r.wert, 0) }];
  }
  const summe = liste.reduce((s, t) => s + t.wert, 0);
  if (!summe) return <p className="leise">{leer}</p>;
  const r = 42;
  const umfang = 2 * Math.PI * r;
  let lauf = 0;
  const gewaehlt = liste.find((t) => t.schluessel === aktiv);
  return (
    <div className="bu-kreis">
      <svg viewBox="0 0 120 120" role="img" aria-label={name}>
        <g transform="rotate(-90 60 60)">
          {liste.map((t, i) => {
            const laenge = (t.wert / summe) * umfang;
            const kreis = (
              <circle
                key={t.schluessel}
                cx="60"
                cy="60"
                r={r}
                fill="none"
                stroke={KREIS_FARBEN[i]}
                strokeWidth={aktiv === t.schluessel ? 20 : 16}
                strokeDasharray={`${Math.max(0, laenge - (liste.length > 1 ? 1.2 : 0))} ${umfang}`}
                strokeDashoffset={-lauf}
                onMouseEnter={() => setAktiv(t.schluessel)}
                onMouseLeave={() => setAktiv(null)}
                className="bu-kreis-teil"
              >
                <title>{`${t.name}: ${betrag(t.wert, waehrung)} (${Math.round((t.wert / summe) * 100)} %)`}</title>
              </circle>
            );
            lauf += laenge;
            return kreis;
          })}
        </g>
        <text x="60" y="56" textAnchor="middle" className="bu-kreis-zahl">
          {gewaehlt ? `${Math.round((gewaehlt.wert / summe) * 100)} %` : 'Gesamt'}
        </text>
        <text x="60" y="70" textAnchor="middle" className="bu-kreis-klein">
          {betrag(gewaehlt ? gewaehlt.wert : summe, waehrung)}
        </text>
      </svg>
      <ul className="bu-legende">
        {liste.map((t, i) => (
          <li key={t.schluessel} onMouseEnter={() => setAktiv(t.schluessel)} onMouseLeave={() => setAktiv(null)} className={aktiv === t.schluessel ? 'aktiv' : ''}>
            <span className="bu-legende-farbe" style={{ background: KREIS_FARBEN[i] }} />
            <span className="bu-legende-name">{t.name}</span>
            <span className="bu-legende-zahl">
              {betrag(t.wert, waehrung)} <span className="leise klein">{Math.round((t.wert / summe) * 100)} %</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Senkrechte Säulen je Monat: Ausgaben und – falls vorhanden – Einnahmen als zweite Säule daneben,
 * mit Hinweis beim Darüberfahren (Antippen am Handy).
 */
export function Saeulen({ zeilen, waehrung, name = 'Ausgaben', zweitName = 'Einnahmen' }: { zeilen: (Wertzeile & { zweit?: number })[]; waehrung: string; name?: string; zweitName?: string }) {
  const [aktiv, setAktiv] = useState<number | null>(null);
  if (!zeilen.length) return <p className="leise">Noch keine Ausgaben oder Einnahmen mit Datum.</p>;
  const mitZweit = zeilen.some((z) => (z.zweit ?? 0) !== 0);
  const max = Math.max(1, ...zeilen.map((z) => Math.max(z.wert, z.zweit ?? 0)));
  const b = 600;
  const h = 200;
  const unten = 24;
  const oben = 10;
  const schritt = b / zeilen.length;
  const breite = Math.max(4, Math.min(mitZweit ? 26 : 48, (mitZweit ? schritt / 2 : schritt) - 6));
  const nurJede = Math.ceil(zeilen.length / 12);
  const gewaehlt = aktiv === null ? null : zeilen[aktiv];
  const hoeheVon = (wert: number) => (Math.max(0, wert) / max) * (h - unten - oben);
  return (
    <div className="bu-saeulen">
      {mitZweit && (
        <div className="bu-legende-zeile leise klein">
          <span>
            <i className="bu-punkt bu-punkt-ist" /> {name}
          </span>
          <span>
            <i className="bu-punkt bu-punkt-ein" /> {zweitName}
          </span>
        </div>
      )}
      <svg viewBox={`0 0 ${b} ${h}`} preserveAspectRatio="none" role="img" aria-label={`${name}${mitZweit ? ` und ${zweitName}` : ''} je Monat`} onMouseLeave={() => setAktiv(null)}>
        <line x1="0" x2={b} y1={h - unten} y2={h - unten} className="bu-achse" />
        <line x1="0" x2={b} y1={oben} y2={oben} className="bu-raster" />
        <line x1="0" x2={b} y1={(h - unten + oben) / 2} y2={(h - unten + oben) / 2} className="bu-raster" />
        {zeilen.map((z, i) => {
          const mitte = i * schritt + schritt / 2;
          const x1 = mitZweit ? mitte - breite - 1 : mitte - breite / 2;
          const h1 = hoeheVon(z.wert);
          const h2 = hoeheVon(z.zweit ?? 0);
          return (
            <g key={z.schluessel} onMouseEnter={() => setAktiv(i)} onClick={() => setAktiv(i)}>
              <rect x={i * schritt} y={0} width={schritt} height={h} fill="transparent" />
              {z.wert > 0 && <rect x={x1} y={h - unten - h1} width={breite} height={Math.max(h1, 1)} rx="3" className={`bu-saeule${aktiv === i ? ' aktiv' : ''}`} />}
              {mitZweit && (z.zweit ?? 0) > 0 && (
                <rect x={mitte + 1} y={h - unten - h2} width={breite} height={Math.max(h2, 1)} rx="3" className={`bu-saeule bu-saeule-ein${aktiv === i ? ' aktiv' : ''}`} />
              )}
              <title>{`${z.name}: ${name} ${betrag(z.wert, waehrung)}${mitZweit ? `, ${zweitName} ${betrag(z.zweit ?? 0, waehrung)}` : ''}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="bu-saeulen-achse" style={{ gridTemplateColumns: `repeat(${zeilen.length}, 1fr)` }}>
        {zeilen.map((z, i) => (
          <span key={z.schluessel}>{i % nurJede === 0 ? z.name : ''}</span>
        ))}
      </div>
      <p className="bu-saeulen-info leise klein">
        {gewaehlt ? (
          <>
            <strong>{gewaehlt.name}:</strong> {name} {betrag(gewaehlt.wert, waehrung)}
            {mitZweit && (
              <>
                {' '}
                · {zweitName} {betrag(gewaehlt.zweit ?? 0, waehrung)} · Saldo {betrag((gewaehlt.zweit ?? 0) - gewaehlt.wert, waehrung)}
              </>
            )}
          </>
        ) : (
          <>Mit der Maus über einen Monat fahren (Handy: antippen) für Details.</>
        )}
      </p>
    </div>
  );
}
